import { makeStyles, TableCell, TableRow } from '@material-ui/core';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { DamageCategory, SourceRegisterCarDetailDamage, SourceType } from '../modules/generated/api';
import { damageCategories, getLabel } from '../modules/labels';

import CarDetailTable from './CarDetailTable';
import CarDetailTableHeadCell from './CarDetailTableHeadCell';
import UnitValue from './UnitValue';

const useStyles = makeStyles((theme) => ({
  categoryRow: {
    backgroundColor: theme.palette.grey[100],
  },
  categoryCell: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: '0.75rem',
    color: theme.palette.grey[800],
    paddingLeft: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    '@media print': {
      pageBreakAfter: 'avoid',
      breakAfter: 'avoid',
    },
  },
  groupedRow: {
    '& > th': {
      paddingLeft: theme.spacing(4),
    },
  },
  repairCostCell: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
}));

type CarDetailDamageTableProps = {
  damages?: SourceRegisterCarDetailDamage[];
  source?: SourceType;
};

/**
 * Grouping only happens for Audi UCP, and only once the BE actually delivers categories
 *
 * Exported because the grouped table is considerably taller and wider than the flat one, so
 * CarDetailContent widens its grid column accordingly. Keep both in sync through this predicate.
 */
export const areDamagesGrouped = (damages?: SourceRegisterCarDetailDamage[], source?: SourceType): boolean =>
  source === SourceType.AudiUcp && !!damages?.some(({ category }) => category);

const CarDetailDamageTable = ({ damages, source }: CarDetailDamageTableProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Repair costs are only delivered by Audi UCP, so only there the third column is rendered.
  const isAudiUCP = source === SourceType.AudiUcp;
  const grouped = areDamagesGrouped(damages, source);

  const renderRows = (rows: SourceRegisterCarDetailDamage[], keyPrefix = '') =>
    rows.map(({ name, repairNote, repairCost }) => (
      <TableRow key={`${keyPrefix}${name};${repairNote}`} className={grouped ? classes.groupedRow : undefined}>
        <CarDetailTableHeadCell>{name}</CarDetailTableHeadCell>
        <TableCell>{repairNote}</TableCell>
        {isAudiUCP && (
          <TableCell className={classes.repairCostCell}>
            <UnitValue unit={repairCost?.currency} value={repairCost?.amount} />
          </TableCell>
        )}
      </TableRow>
    ));

  // Uncategorized damages are shown under "other" as soon as at least one damage is categorized.
  const groups = damageCategories
    .map(({ value }) => ({
      category: value,
      rows: (damages ?? []).filter(
        ({ category }) => category === value || (value === DamageCategory.Other && !category),
      ),
    }))
    .filter(({ rows }) => rows.length > 0);

  return (
    <CarDetailTable title={t('car.damage_plural')}>
      {grouped
        ? groups.map(({ category, rows }) => (
            <Fragment key={category}>
              <TableRow className={classes.categoryRow}>
                <TableCell className={classes.categoryCell} colSpan={3}>
                  {getLabel(damageCategories, category)}
                </TableCell>
              </TableRow>
              {renderRows(rows, `${category};`)}
            </Fragment>
          ))
        : renderRows(damages ?? [])}
    </CarDetailTable>
  );
};

export default CarDetailDamageTable;
